import type { ThemeToken, Typography } from '../store/types';

function slug(s: string): string {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function stripQuotes(v: string): string {
  const t = v.trim();
  return t.replace(/^["']|["']$/g, '');
}

/** Parse the indentation-structured front matter into a nested plain object.
 *  Scoped to the design.md token shape: 2-space indent levels. */
function parseFrontMatter(fm: string): Record<string, unknown> {
  const lines = fm.split('\n').filter((l) => l.trim() !== '');
  const root: Record<string, unknown> = {};
  const stack: { indent: number; obj: Record<string, unknown> }[] = [{ indent: -1, obj: root }];
  for (const line of lines) {
    const indent = line.length - line.trimStart().length;
    const m = line.trim().match(/^([\w-]+):\s*(.*)$/);
    if (!m) continue;
    const [, key, rawVal] = m;
    while (stack.length > 1 && indent <= stack[stack.length - 1].indent) stack.pop();
    const parent = stack[stack.length - 1].obj;
    if (rawVal === '') {
      const child: Record<string, unknown> = {};
      parent[key] = child;
      stack.push({ indent, obj: child });
    } else {
      parent[key] = stripQuotes(rawVal);
    }
  }
  return root;
}

export function parseDesignMd(source: string): ThemeToken {
  const m = source.match(/^---\n([\s\S]*?)\n---/);
  if (!m) throw new Error('design.md: missing YAML front matter');
  const fm = parseFrontMatter(m[1]) as Record<string, any>;
  const name = String(fm.name ?? 'Untitled');
  const typography: Record<string, Typography> = {};
  for (const [k, v] of Object.entries(fm.typography ?? {})) typography[k] = v as Typography;
  return {
    id: slug(name),
    name,
    description: fm.description,
    colors: (fm.colors ?? {}) as Record<string, string>,
    typography,
    rounded: (fm.rounded ?? {}) as Record<string, string>,
    spacing: (fm.spacing ?? {}) as Record<string, string | number>,
    components: fm.components as ThemeToken['components'],
    shadow: fm.shadow as Record<string, string> | undefined,
    motion: fm.motion as Record<string, string> | undefined,
  };
}

function emitGroup(name: string, obj: Record<string, unknown> | undefined): string[] {
  if (!obj || Object.keys(obj).length === 0) return [];
  const out = [`${name}:`];
  for (const [k, v] of Object.entries(obj)) {
    if (v && typeof v === 'object') {
      out.push(`  ${k}:`);
      for (const [k2, v2] of Object.entries(v as Record<string, unknown>)) out.push(`    ${k2}: ${v2}`);
    } else {
      const needsQuote = typeof v === 'string' && v.startsWith('#');
      out.push(`  ${k}: ${needsQuote ? `"${v}"` : v}`);
    }
  }
  return out;
}

export function serializeDesignMd(t: ThemeToken): string {
  const fm = [
    `name: ${t.name}`,
    ...(t.description ? [`description: ${t.description}`] : []),
    ...emitGroup('colors', t.colors),
    ...emitGroup('typography', t.typography as Record<string, unknown>),
    ...emitGroup('rounded', t.rounded),
    ...emitGroup('spacing', t.spacing as Record<string, unknown>),
    ...emitGroup('components', t.components as Record<string, unknown> | undefined),
  ].join('\n');
  return `---\n${fm}\n---\n\n# ${t.name}\n`;
}
